import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import './contact-modal.css'

const ContactModal = ({ isOpen, onClose, buttonSource = 'Unbekannt' }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: '',
    buttonSource: buttonSource
  })
  const [errors, setErrors] = useState({})
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitStatus, setSubmitStatus] = useState(null)

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden'
      setFormData(prev => ({ ...prev, buttonSource: buttonSource }))
    } else {
      document.body.style.overflow = 'unset'
    }

    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen) {
        onClose()
      }
    }

    document.addEventListener('keydown', handleEscape)
    return () => {
      document.removeEventListener('keydown', handleEscape)
      document.body.style.overflow = 'unset'
    }
  }, [isOpen, onClose])

  const validateForm = () => {
    const newErrors = {}

    if (!formData.name.trim()) {
      newErrors.name = 'Bitte geben Sie Ihren Namen ein'
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Bitte geben Sie Ihre E-Mail-Adresse ein'
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Bitte geben Sie eine gültige E-Mail-Adresse ein'
    }

    if (!formData.message.trim()) {
      newErrors.message = 'Bitte geben Sie eine Nachricht ein'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    if (!validateForm()) {
      return
    }

    setIsSubmitting(true)
    setSubmitStatus(null)

    try {
      // Send to our serverless function instead of directly to Slack
      const response = await fetch('/api/send-slack-message', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          message: formData.message,
          buttonSource: formData.buttonSource
        })
      })

      if (!response.ok) {
        const error = await response.json()
        throw new Error(error.error || 'Failed to send message')
      }

      console.log('Form submitted:', formData)
      
      setSubmitStatus('success')
      setFormData({ name: '', email: '', message: '', buttonSource: buttonSource })
      
      // Close modal after success
      setTimeout(() => {
        onClose()
        setSubmitStatus(null)
      }, 2000)
    } catch (error) {
      console.error('Form submission error:', error)
      setSubmitStatus('error')
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({ ...prev, [name]: value }))
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }))
    }
  }

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose()
    }
  }

  if (!isOpen) return null

  return (
    <div className="contact-modal-overlay" onClick={handleBackdropClick}>
      <div className="contact-modal-container">
        <button 
          className="contact-modal-close" 
          onClick={onClose}
          aria-label="Schließen"
          type="button"
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M18 6L6 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </button>

        <div className="contact-modal-header">
          <h2 className="contact-modal-title">Kostenlose Beratung anfragen</h2>
          <p className="contact-modal-subtitle">
            Füllen Sie das Formular aus und wir melden uns innerhalb von 24 Stunden bei Ihnen.
          </p>
          {buttonSource && buttonSource !== 'Unbekannt' && (
            <div className="contact-modal-source">
              <span className="source-label">Ihre Anfrage:</span>
              <span className="source-value">{buttonSource}</span>
            </div>
          )}
        </div>

        {submitStatus === 'success' ? (
          <div className="contact-modal-success">
            <div className="success-icon">
              <svg width="64" height="64" viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="32" cy="32" r="32" fill="#9FEF00"/>
                <path d="M20 32L28 40L44 24" stroke="#0F0F0F" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <h3>Vielen Dank!</h3>
            <p>Ihre Anfrage wurde erfolgreich gesendet. Wir melden uns in Kürze bei Ihnen.</p>
          </div>
        ) : (
          <form className="contact-modal-form" onSubmit={handleSubmit}>
            <div className="contact-modal-field">
              <label htmlFor="name" className="contact-modal-label">
                Name *
              </label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                className={`contact-modal-input ${errors.name ? 'error' : ''}`}
                placeholder="Ihr vollständiger Name"
                disabled={isSubmitting}
              />
              {errors.name && <span className="contact-modal-error">{errors.name}</span>}
            </div>

            <div className="contact-modal-field">
              <label htmlFor="email" className="contact-modal-label">
                E-Mail *
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                className={`contact-modal-input ${errors.email ? 'error' : ''}`}
                placeholder="ihre@email.de"
                disabled={isSubmitting}
              />
              {errors.email && <span className="contact-modal-error">{errors.email}</span>}
            </div>

            <div className="contact-modal-field">
              <label htmlFor="message" className="contact-modal-label">
                Nachricht *
              </label>
              <textarea
                id="message"
                name="message"
                value={formData.message}
                onChange={handleChange}
                className={`contact-modal-textarea ${errors.message ? 'error' : ''}`}
                placeholder="Beschreiben Sie Ihre Anforderungen oder stellen Sie Ihre Fragen..."
                rows="5"
                disabled={isSubmitting}
              />
              {errors.message && <span className="contact-modal-error">{errors.message}</span>}
            </div>

            {submitStatus === 'error' && (
              <div className="contact-modal-error-message">
                Ein Fehler ist aufgetreten. Bitte versuchen Sie es später erneut.
              </div>
            )}

            <button 
              type="submit" 
              className="contact-modal-submit"
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <span className="spinner"></span>
                  Wird gesendet...
                </>
              ) : (
                'Kostenlose Beratung anfragen'
              )}
            </button>

            <p className="contact-modal-privacy">
              Mit dem Absenden stimmen Sie unseren Datenschutzbestimmungen zu.
            </p>
          </form>
        )}
      </div>
    </div>
  )
}

ContactModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  buttonSource: PropTypes.string,
}

export default ContactModal
